import { alpha } from '@mui/material/styles';
import { JLR_PLANT } from '../../constants';

export default function Chip(theme) {
  const isJlr = theme.plantName === JLR_PLANT;
  const isDarkMode = theme.palette.mode === 'dark';

  const jlrChipStyles = {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 500,
    borderRadius: '12px',
    fontSize: '0.8125rem',
    letterSpacing: '0.25px',
    height: 'auto',
    minHeight: '32px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: isDarkMode 
        ? '0 4px 12px rgba(0, 0, 0, 0.2)'
        : '0 4px 12px rgba(45, 74, 45, 0.15)',
    },

    '& .MuiChip-icon': {
      fontSize: '1rem',
      marginLeft: '8px',
      marginRight: '-4px',
    },

    '& .MuiChip-deleteIcon': {
      fontSize: '1.125rem',
      marginRight: '8px',
      marginLeft: '-4px',
      
      '&:hover': {
        color: 'inherit',
        opacity: 0.8,
      },
    },

    '& .MuiChip-label': {
      padding: '6px 12px',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      letterSpacing: 'inherit',
    },
  };

  const standardChipStyles = {
    borderRadius: '16px',
    fontSize: '0.8125rem',
  };

  const baseStyles = isJlr ? jlrChipStyles : standardChipStyles;

  return {
    MuiChip: {
      styleOverrides: {
        root: baseStyles,

        // Filled variants
        filled: {
          ...(isJlr && {
            background: isDarkMode 
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(45, 74, 45, 0.1)',
            color: isDarkMode ? '#ffffff' : '#2d4a2d',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(255, 255, 255, 0.15)'
                : 'rgba(45, 74, 45, 0.15)',
            },
          }),
        },

        // Outlined variants
        outlined: {
          ...(isJlr && {
            borderColor: isDarkMode 
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(45, 74, 45, 0.2)',
            color: isDarkMode ? '#ffffff' : '#2d4a2d',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(45, 74, 45, 0.05)',
              borderColor: isDarkMode 
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(45, 74, 45, 0.3)',
            },
          }),
        },

        // Color variants for JLR
        ...(isJlr && {
          colorPrimary: {
            background: isDarkMode 
              ? 'rgba(0, 102, 204, 0.2)'
              : 'rgba(45, 74, 45, 0.15)',
            color: isDarkMode ? '#0066cc' : '#2d4a2d',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(0, 102, 204, 0.3)'
                : 'rgba(45, 74, 45, 0.25)',
            },
          },

          colorSecondary: {
            background: isDarkMode 
              ? 'rgba(200, 185, 156, 0.2)'
              : 'rgba(200, 185, 156, 0.15)',
            color: isDarkMode ? '#c8b99c' : '#9d8b6f',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(200, 185, 156, 0.3)'
                : 'rgba(200, 185, 156, 0.25)',
            },
          },

          colorSuccess: {
            background: isDarkMode 
              ? 'rgba(39, 174, 96, 0.2)'
              : 'rgba(39, 174, 96, 0.15)',
            color: '#27ae60',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(39, 174, 96, 0.3)'
                : 'rgba(39, 174, 96, 0.25)',
            },
          },

          colorError: {
            background: isDarkMode 
              ? 'rgba(220, 53, 69, 0.2)'
              : 'rgba(220, 53, 69, 0.15)',
            color: '#dc3545',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(220, 53, 69, 0.3)'
                : 'rgba(220, 53, 69, 0.25)',
            },
          },

          colorWarning: {
            background: isDarkMode 
              ? 'rgba(255, 167, 38, 0.2)'
              : 'rgba(255, 167, 38, 0.15)',
            color: '#ffa726',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(255, 167, 38, 0.3)'
                : 'rgba(255, 167, 38, 0.25)',
            },
          },

          colorInfo: {
            background: isDarkMode 
              ? 'rgba(25, 118, 210, 0.2)'
              : 'rgba(25, 118, 210, 0.15)',
            color: '#1976d2',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(25, 118, 210, 0.3)'
                : 'rgba(25, 118, 210, 0.25)',
            },
          },
        }),

        // Size variants
        sizeSmall: {
          height: isJlr ? '24px' : '24px',
          fontSize: isJlr ? '0.75rem' : '0.6875rem',
          
          '& .MuiChip-label': {
            padding: isJlr ? '4px 8px' : '2px 8px',
          },
          
          '& .MuiChip-icon': {
            fontSize: isJlr ? '0.875rem' : '0.75rem',
            marginLeft: isJlr ? '6px' : '4px',
          },
        },

        sizeMedium: {
          height: isJlr ? '32px' : '32px',
          fontSize: isJlr ? '0.8125rem' : '0.8125rem',
        },

        // Clickable state
        clickable: {
          cursor: 'pointer',
          
          '&:hover': {
            ...(isJlr && {
              transform: 'translateY(-2px)',
            }),
          },

          '&:active': {
            ...(isJlr && {
              transform: 'translateY(0)',
            }),
          },
        },

        // Deletable state
        deletable: {
          '& .MuiChip-deleteIcon': {
            ...(isJlr && {
              transition: 'all 0.2s ease',
              
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }),
          },
        },
      },
    },
  };
}
